import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Save, Calendar, Pencil } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { getGoalShow, updateGoal, applyAllGoalSellers } from "@/Api/goals";
import { Mask } from "@/utils/mask";
import { Goal, GoalSeller } from "@/types/goal";
import { ScrollArea } from "@/components/ui/scroll-area";

const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

interface UpdateGoalProps {
  uuid: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => Promise<void>;
}

interface SellerValue {
  id: number;
  target_value: string;
  super_target_value: string;
}

export function UpdateGoal({ uuid, isOpen, onClose, onUpdate }: UpdateGoalProps) {
  const [goal, setGoal] = useState<Goal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sellerValues, setSellerValues] = useState<SellerValue[]>([]);
  const [applyAllTarget, setApplyAllTarget] = useState("");
  const [applyAllSuperTarget, setApplyAllSuperTarget] = useState("");
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [workingDays, setWorkingDays] = useState<number>(0);
  const [selectedWorkingDays, setSelectedWorkingDays] = useState<string[]>([]);
  const [isEditingWorkingDays, setIsEditingWorkingDays] = useState(false);
  const [unityTargetValue, setUnityTargetValue] = useState("");
  const [unitySuperTargetValue, setUnitySuperTargetValue] = useState("");
  const [target100Value, setTarget100Value] = useState("");
  const [superTarget100Value, setSuperTarget100Value] = useState("");

  // Gerar lista de dias no período agrupados por mês
  const { daysInPeriod, daysByMonth } = useMemo(() => {
    if (!startDate || !endDate) return { daysInPeriod: [], daysByMonth: [] };

    const start = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");

    if (start > end) return { daysInPeriod: [], daysByMonth: [] };

    const days: { date: Date; key: string; dayOfWeek: number }[] = [];
    const current = new Date(start);

    while (current <= end) {
      days.push({
        date: new Date(current),
        key: current.toISOString().split("T")[0],
        dayOfWeek: current.getDay(),
      });
      current.setDate(current.getDate() + 1);
    }

    // Agrupar por mês
    const grouped: { month: number; year: number; days: typeof days }[] = [];
    days.forEach((day) => {
      const month = day.date.getMonth();
      const year = day.date.getFullYear();
      const existing = grouped.find((g) => g.month === month && g.year === year);
      if (existing) {
        existing.days.push(day);
      } else {
        grouped.push({ month, year, days: [day] });
      }
    });

    return { daysInPeriod: days, daysByMonth: grouped };
  }, [startDate, endDate]);

  // Quando as datas mudam, recalcular dias úteis selecionados
  useEffect(() => {
    if (daysInPeriod.length > 0 && isEditingWorkingDays && selectedWorkingDays.length === 0) {
      // Pré-selecionar dias úteis (segunda a sábado)
      const workingDayKeys = daysInPeriod
        .filter((day) => day.dayOfWeek !== 0)
        .map((day) => day.key);
      setSelectedWorkingDays(workingDayKeys);
      setWorkingDays(workingDayKeys.length);
    }
  }, [daysInPeriod, isEditingWorkingDays]);

  const handleWorkingDayToggle = (dayKey: string, checked: boolean) => {
    const newSelected = checked
      ? [...selectedWorkingDays, dayKey]
      : selectedWorkingDays.filter((key) => key !== dayKey);

    setSelectedWorkingDays(newSelected);
    setWorkingDays(newSelected.length);
  };

  const handleSelectWeekdays = () => {
    const weekdayKeys = daysInPeriod
      .filter((day) => day.dayOfWeek !== 0)
      .map((day) => day.key);
    setSelectedWorkingDays(weekdayKeys);
    setWorkingDays(weekdayKeys.length);
  };

  const handleSelectAllWorkingDays = () => {
    if (selectedWorkingDays.length === daysInPeriod.length) {
      setSelectedWorkingDays([]);
      setWorkingDays(0);
    } else {
      const allDays = daysInPeriod.map((d) => d.key);
      setSelectedWorkingDays(allDays);
      setWorkingDays(allDays.length);
    }
  };

  useEffect(() => {
    const loadGoal = async () => {
      if (!isOpen || !uuid) return;

      setIsLoading(true);
      try {
        const response = await getGoalShow(uuid);
        if (response.data?.response) {
          const goalData = response.data.response;
          setGoal(goalData);

          // Formatar datas para o input type="date"
          const formatDateForInput = (dateStr: string) => {
            if (!dateStr) return "";
            return dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
          };
          setStartDate(formatDateForInput(goalData.start_date));
          setEndDate(formatDateForInput(goalData.end_date));
          setWorkingDays(goalData.working_days || 0);

          // Carregar dias úteis selecionados do backend
          if (goalData.selected_working_days && goalData.selected_working_days.length > 0) {
            setSelectedWorkingDays(goalData.selected_working_days);
          }

          // Carregar Meta 100% e Super Meta 100%
          setTarget100Value(
            goalData.target_100_value ? Mask.formatCurrency(goalData.target_100_value) : ""
          );
          setSuperTarget100Value(
            goalData.super_target_100_value ? Mask.formatCurrency(goalData.super_target_100_value) : ""
          );

          if (goalData.goal_sellers) {
            setSellerValues(
              goalData.goal_sellers.map((seller: GoalSeller) => ({
                id: seller.id,
                target_value: seller.target_value
                  ? Mask.formatCurrency(seller.target_value)
                  : "",
                super_target_value: seller.super_target_value
                  ? Mask.formatCurrency(seller.super_target_value)
                  : "",
              }))
            );
          }

          // Se não há vendedores, carregar o target_value e super_target_value da unidade
          if (!goalData.goal_sellers || goalData.goal_sellers.length === 0) {
            setUnityTargetValue(
              goalData.target_value ? Mask.formatCurrency(goalData.target_value) : ""
            );
            setUnitySuperTargetValue(
              goalData.super_target_value ? Mask.formatCurrency(goalData.super_target_value) : ""
            );
          }
        }
      } catch (error) {
        console.error("Erro ao carregar meta:", error);
        toast.error("Erro ao carregar meta");
      } finally {
        setIsLoading(false);
      }
    };

    loadGoal();
  }, [isOpen, uuid]);

  const handleSellerValueChange = (
    sellerId: number,
    field: "target_value" | "super_target_value",
    value: string
  ) => {
    setSellerValues((prev) =>
      prev.map((seller) =>
        seller.id === sellerId ? { ...seller, [field]: value } : seller
      )
    );
  };

  const handleApplyAll = async () => {
    if (!applyAllTarget && !applyAllSuperTarget) {
      toast.error("Informe pelo menos um valor para aplicar");
      return;
    }

    setIsApplyingAll(true);
    try {
      const data: { target_value?: number; super_target_value?: number } = {};

      if (applyAllTarget) {
        data.target_value = Number(applyAllTarget.replace(/\D/g, "")) / 100;
      }
      if (applyAllSuperTarget) {
        data.super_target_value =
          Number(applyAllSuperTarget.replace(/\D/g, "")) / 100;
      }

      const response = await applyAllGoalSellers(uuid, data);

      if (response.ok) {
        toast.success("Valores aplicados com sucesso!");

        // Atualizar valores locais
        setSellerValues((prev) =>
          prev.map((seller) => ({
            ...seller,
            target_value: applyAllTarget || seller.target_value,
            super_target_value: applyAllSuperTarget || seller.super_target_value,
          }))
        );

        // Recarregar dados
        const reloadResponse = await getGoalShow(uuid);
        if (reloadResponse.data?.response) {
          setGoal(reloadResponse.data.response);
        }
      } else {
        toast.error("Erro ao aplicar valores");
      }
    } catch (error) {
      toast.error("Erro ao aplicar valores");
    } finally {
      setIsApplyingAll(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const sellers = sellerValues.map((seller) => ({
        id: seller.id,
        target_value: seller.target_value
          ? Number(seller.target_value.replace(/\D/g, "")) / 100
          : null,
        super_target_value: seller.super_target_value
          ? Number(seller.super_target_value.replace(/\D/g, "")) / 100
          : null,
      }));

      const hasNoSellers = !goal?.goal_sellers || goal.goal_sellers.length === 0;

      const updateData: any = {
        start_date: startDate,
        end_date: endDate,
        working_days: workingDays,
        selected_working_days: selectedWorkingDays.length > 0 ? selectedWorkingDays : null,
        target_100_value: target100Value ? Number(target100Value.replace(/\D/g, "")) / 100 : null,
        super_target_100_value: superTarget100Value ? Number(superTarget100Value.replace(/\D/g, "")) / 100 : null,
        sellers,
      };

      // Se não há vendedores, enviar o target_value e super_target_value da unidade
      if (hasNoSellers) {
        if (unityTargetValue) {
          updateData.target_value = Number(unityTargetValue.replace(/\D/g, "")) / 100;
        }
        if (unitySuperTargetValue) {
          updateData.super_target_value = Number(unitySuperTargetValue.replace(/\D/g, "")) / 100;
        }
      }

      const response = await updateGoal(uuid, updateData);

      if (response.ok) {
        toast.success("Meta atualizada com sucesso!");
        if (onUpdate) {
          await onUpdate();
        }
        onClose();
      } else {
        toast.error("Erro ao atualizar meta");
      }
    } catch (error) {
      toast.error("Erro ao atualizar meta");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Gerenciar Meta - {goal?.unity?.name}
          </DialogTitle>
          <DialogDescription>
            {goal?.goal_sellers && goal.goal_sellers.length > 0
              ? "Configure as metas individuais para cada vendedor."
              : "Configure a meta de vendas para esta unidade."}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : goal ? (
          <div className="space-y-4">
            {/* Informações da Meta */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-4">
              {/* Linha 1: Datas, Dias Úteis e Status */}
              <div className="flex flex-wrap items-end gap-6">
                <div className="space-y-1">
                  <Label className="text-sm text-muted-foreground">Data Início</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-muted-foreground">Data Fim</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-muted-foreground">Dias Úteis</Label>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-lg">{workingDays}</span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setIsEditingWorkingDays(!isEditingWorkingDays)}
                      className="h-7 w-7 p-0 bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Status</span>
                  <div className="mt-1">
                    <Badge
                      style={{
                        backgroundColor: goal.goal_status?.color,
                        color: "white",
                      }}
                    >
                      {goal.goal_status?.name}
                    </Badge>
                  </div>
                </div>
              </div>
              {/* Linha 2: Meta Total e Vendido */}
              <div className="flex flex-wrap gap-8 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <span className="text-sm text-muted-foreground">Meta Total</span>
                  <p className="font-semibold text-xl whitespace-nowrap">na 
                    {Mask.formatCurrency(goal.target_value)}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Vendido</span>
                  <p className="font-semibold text-xl text-green-600 whitespace-nowrap">
                    {Mask.formatCurrency(goal.sold_value || 0)}
                  </p>
                </div>
              </div>
            </div>

            {/* Meta 100% e Super Meta 100% */}
            <div className="p-4 border rounded-lg bg-amber-50 dark:bg-amber-900/20">
              <p className="text-xs text-muted-foreground mb-3">Valores de referência para cálculo de premiação</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Meta Individual 100%</Label>
                  <Input
                    typeMask="currency"
                    value={target100Value}
                    onChange={(e) => setTarget100Value(e.target.value)}
                    placeholder="R$ 0,00"
                    className="text-right"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Super Meta 100%</Label>
                  <Input
                    typeMask="currency"
                    value={superTarget100Value}
                    onChange={(e) => setSuperTarget100Value(e.target.value)}
                    placeholder="R$ 0,00"
                    className="text-right"
                  />
                </div>
              </div>
            </div>

            {/* Editor de Dias Úteis */}
            {isEditingWorkingDays && daysInPeriod.length > 0 && (
              <div className="p-4 border rounded-lg bg-violet-50 dark:bg-violet-900/20">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Selecionar Dias Úteis ({selectedWorkingDays.length} selecionados)
                  </h4>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectWeekdays}
                      className="h-auto py-1 px-2 text-xs"
                    >
                      Seg-Sáb
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAllWorkingDays}
                      className="h-auto py-1 px-2 text-xs"
                    >
                      {selectedWorkingDays.length === daysInPeriod.length
                        ? "Desmarcar"
                        : "Todos"}
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-[200px] border rounded-md p-3 bg-white dark:bg-gray-900">
                  <div className="space-y-4">
                    {daysByMonth.map(({ month, year, days }) => (
                      <div key={`${year}-${month}`}>
                        <div className="text-sm font-semibold text-center mb-2 text-gray-700 dark:text-gray-300">
                          {monthNames[month]} {year}
                        </div>
                        <div className="grid grid-cols-7 gap-1 mb-1">
                          {dayNames.map((name, index) => (
                            <div
                              key={name}
                              className={`text-center text-[10px] font-semibold ${
                                index === 0 ? "text-red-500" : "text-gray-500 dark:text-gray-400"
                              }`}
                            >
                              {name}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {Array.from({ length: days[0]?.dayOfWeek || 0 }).map((_, i) => (
                            <div key={`empty-${month}-${i}`} className="h-8" />
                          ))}
                          {days.map((day) => {
                            const isSelected = selectedWorkingDays.includes(day.key);
                            const isSunday = day.dayOfWeek === 0;
                            return (
                              <button
                                key={day.key}
                                type="button"
                                onClick={() => handleWorkingDayToggle(day.key, !isSelected)}
                                className={`
                                  h-8 w-full rounded-md text-sm font-medium transition-all
                                  ${isSelected
                                    ? "bg-primary text-white hover:bg-primary/90"
                                    : isSunday
                                      ? "bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40"
                                      : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                                  }
                                `}
                              >
                                {day.date.getDate()}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Aplicar em Todos - só mostra se há vendedores */}
            {goal.goal_sellers && goal.goal_sellers.length > 0 && (
            <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <h4 className="font-medium mb-3">Aplicar valores para todos os vendedores</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <Label>Meta Individual</Label>
                  <Input
                    typeMask="currency"
                    value={applyAllTarget}
                    onChange={(e) => setApplyAllTarget(e.target.value)}
                    placeholder="R$ 0,00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Super Meta</Label>
                  <Input
                    typeMask="currency"
                    value={applyAllSuperTarget}
                    onChange={(e) => setApplyAllSuperTarget(e.target.value)}
                    placeholder="R$ 0,00"
                  />
                </div>
                <Button
                  onClick={handleApplyAll}
                  disabled={isApplyingAll}
                  variant="secondary"
                >
                  {isApplyingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Aplicar em Todos
                </Button>
              </div>
            </div>
            )}

            {/* Tabela de Vendedores - só mostra se há vendedores */}
            {goal.goal_sellers && goal.goal_sellers.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[300px] overflow-auto">
                  <Table className="table-fixed w-full">
                    <TableHeader className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                      <TableRow>
                        <TableHead className="w-[25%]">Vendedor</TableHead>
                        <TableHead className="w-[15%]">Meta</TableHead>
                        <TableHead className="w-[15%]">Super Meta</TableHead>
                        <TableHead className="w-[15%] text-right">Vendido</TableHead>
                        <TableHead className="w-[15%] text-right">Falta</TableHead>
                        <TableHead className="w-[15%] text-right">Média/Dia</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {goal.goal_sellers.map((seller) => {
                        const sellerValue = sellerValues.find(
                          (sv) => sv.id === seller.id
                        );
                        return (
                          <TableRow key={seller.id}>
                            <TableCell className="font-medium truncate">
                              {seller.seller?.user?.name}
                            </TableCell>
                            <TableCell>
                              <Input
                                typeMask="currency"
                                value={sellerValue?.target_value || ""}
                                onChange={(e) =>
                                  handleSellerValueChange(
                                    seller.id,
                                    "target_value",
                                    e.target.value
                                  )
                                }
                                className="w-full text-right"
                                placeholder="R$ 0,00"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                typeMask="currency"
                                value={sellerValue?.super_target_value || ""}
                                onChange={(e) =>
                                  handleSellerValueChange(
                                    seller.id,
                                    "super_target_value",
                                    e.target.value
                                  )
                                }
                                className="w-full text-right"
                                placeholder="R$ 0,00"
                              />
                            </TableCell>
                            <TableCell className="text-right text-green-600 font-medium">
                              {Mask.formatCurrency(seller.sold_value || 0)}
                            </TableCell>
                            <TableCell className="text-right text-orange-600">
                              {seller.remaining_value != null
                                ? Mask.formatCurrency(seller.remaining_value)
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {Mask.formatCurrency(seller.daily_average || 0)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              /* Layout para Meta da Unidade quando não há vendedores */
              <div className="border rounded-lg p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                <div className="text-center space-y-6">
                  <div className="space-y-2">
                    <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                      <Users className="w-8 h-8 text-blue-600 dark:text-blue-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                      Meta da Unidade
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Esta unidade não possui vendedores cadastrados. Defina a meta de vendas diretamente para a unidade.
                    </p>
                  </div>

                  <div className="max-w-md mx-auto space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-base font-medium">Valor da Meta</Label>
                        <Input
                          typeMask="currency"
                          value={unityTargetValue}
                          onChange={(e) => setUnityTargetValue(e.target.value)}
                          placeholder="R$ 0,00"
                          className="text-center text-lg h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-base font-medium">Valor da Super Meta</Label>
                        <Input
                          typeMask="currency"
                          value={unitySuperTargetValue}
                          onChange={(e) => setUnitySuperTargetValue(e.target.value)}
                          placeholder="R$ 0,00"
                          className="text-center text-lg h-12"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Vendido</p>
                        <p className="text-lg font-semibold text-green-600">
                          {Mask.formatCurrency(goal.sold_value || 0)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Falta</p>
                        <p className="text-lg font-semibold text-orange-600">
                          {Mask.formatCurrency(goal.remaining_value || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="bg-primary hover:bg-primary/90"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar Alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
